// FILE: src/utils/letterReferences.js
//
// Letter-reference consistency for multiple-choice items.
//
// An explanation (or stem, or a choice) may refer to options BY LETTER —
// "B is correct: …", "A is tempting because…", "(C) risks…", "Option D ignores…".
// Any transformation that moves choices between letter slots, or changes the
// stored key, without rewriting that prose silently corrupts the item: the key
// still grades correctly, but the explanation names the wrong options. This is
// exactly what the Question Factory answer-key balancer did to 467 published
// questions before migration 102 (audit: docs/key-balancer-audit-2026-09-16.md).
//
// These helpers are the CLIENT half of the permanent guard. The DB half is the
// questions_letter_reference_guard trigger (migration 103), which enforces the
// same two rules on every write regardless of path:
//
//   Rule 1 (asserted key): letters the prose ASSERTS as correct
//           ("B is correct", "B is the correct answer", "the correct answer is B")
//           must all be in correctAnswers.
//   Rule 2 (reorder): if choices are reordered while the prose is unchanged, no
//           referenced letter may be one that moved.
//
// Scope: single-letter labels A–E only (the classic multiple_choice /
// multiple_select layout). Interactive types use different ids and are skipped.

// Verbs that make a bare capital letter read as an option label rather than
// the article "A": "A is tempting", "A would…", "A fails to…".
const LABEL_VERBS = new Set((
  'is was are would could should will remains represents describes reflects addresses ' +
  'ignores accepts assumes treats misses fails skips delays risks over under jumps focuses ' +
  'proposes suggests recommends introduces conflates confuses violates sounds seems looks ' +
  'appears feels might may does only merely simply correctly incorrectly wrongly'
).split(' '));

const LABEL_NOUNS = ['distractor', 'option', 'choice', 'answer', 'alternative', 'response'];

/**
 * Letters (upper-case) that `text` references as option labels.
 * B–E: any standalone upper-case token counts. A: only when it behaves like a
 * label — "(A)", "A:", "A.", followed by a label verb, or preceded by
 * Option/Choice/Answer/Distractor. Lower-case letters count only as "(b)" or
 * "option b".
 * @param {string} text
 * @returns {string[]} sorted unique letters
 */
export function extractLetterReferences(text) {
  if (!text || typeof text !== 'string') return [];
  const out = new Set();
  const re = /\(?([A-Ea-e])\)?/g;
  let m;
  while ((m = re.exec(text)) !== null) {
    const start = m.index;                       // index of "(" or the letter
    const end = start + m[0].length;
    const prevCh = start > 0 ? text[start - 1] : '';
    const nextCh = end < text.length ? text[end] : '';
    if (/[A-Za-z0-9]/.test(prevCh) || /[A-Za-z0-9]/.test(nextCh)) continue;   // inside a word
    const paren = m[0].startsWith('(');
    const after = text.slice(end, end + 30);
    const before = text.slice(Math.max(0, start - 14), start).toLowerCase().trimEnd();
    const nounBefore = LABEL_NOUNS.some((n) => before.endsWith(n));
    const isLower = m[1] !== m[1].toUpperCase();
    if (isLower && !(paren || nounBefore)) continue;
    if (m[1] === 'A') {
      const nextWord = (after.match(/^[:.]?\s*([\w'-]+)/) || [])[1];
      const isLabel = paren || after.startsWith(':') ||
        (nextWord && LABEL_VERBS.has(nextWord.toLowerCase())) || nounBefore;
      if (!isLabel) continue;
    }
    out.add(m[1].toUpperCase());
  }
  return [...out].sort();
}

const ASSERT_PATTERNS = [
  /^\s*\(?([A-E])\)?[:.]?\s+(?:is|was)\s+(?:the\s+)?(?:correct|right|best)\b/gi,
  // "X is the right answer in some programs, but…" is hedged distractor talk,
  // so the noun must be followed by punctuation / because / since / here / end.
  /(?:^|[^A-Za-z0-9])\(?([A-E])\)?\s+is\s+the\s+(?:correct|right|best)\s+(?:answer|option|choice|response)(?:[.,:;!]|\s+(?:because|since|as|here)\b|\s*$)/gi,
  /(?:^|[^A-Za-z0-9])\(?([A-E])\)?\s+is\s+correct\s+(?:because|since|as)\b/gi,
  /\b(?:option|choice|answer)\s+\(?([A-E])\)?\s+(?:is|was)\s+(?:the\s+)?(?:correct|right|best)\b/gi,
  /\bcorrect\s+(?:answer|option|choice|response)\s+(?:is|would be)[:\s]+\(?([A-E])\)?(?![A-Za-z])/gi,
];

/**
 * Letters the prose asserts to be THE correct answer (tight patterns only —
 * distractor commentary is ignored). Upper-case, sorted, unique.
 * @param {string} text
 * @returns {string[]}
 */
export function extractAssertedCorrectLetters(text) {
  if (!text || typeof text !== 'string') return [];
  const out = new Set();
  for (const re of ASSERT_PATTERNS) {
    re.lastIndex = 0;
    let m;
    while ((m = re.exec(text)) !== null) out.add(m[1].toUpperCase());
  }
  return [...out].sort();
}

const isLetterLayout = (choices) =>
  Array.isArray(choices) && choices.length > 0 &&
  choices.every((c) => c && typeof c.id === 'string' && /^[A-E]$/.test(c.id));

/**
 * Rule 1 — asserted letters must be in the key. Returns null when consistent
 * (or not applicable), else a human-readable error.
 * @param {{ explanation?: string, choices?: Array<{id:string,text:string}>, correctAnswers?: string[] }} q
 * @returns {string|null}
 */
export function checkAssertedKey(q) {
  if (!q || !isLetterLayout(q.choices)) return null;
  const asserted = extractAssertedCorrectLetters(q.explanation);
  if (asserted.length === 0) return null;
  const key = new Set((q.correctAnswers || []).map((k) => String(k).toUpperCase()));
  const wrong = asserted.filter((L) => !key.has(L));
  if (wrong.length === 0) return null;
  return `The explanation names ${wrong.join(', ')} as the correct answer but the answer key is ${[...key].sort().join(', ') || '(empty)'}. Fix the key or rewrite the explanation.`;
}

/**
 * Rule 2 — a reorder must not leave stale letter references. Compares a
 * previous and a next version of the same item.
 * @param {object} prev  { question, explanation, choices, correctAnswers }
 * @param {object} next  same shape
 * @returns {string|null}
 */
export function checkReorderAgainstReferences(prev, next) {
  if (!prev || !next || !isLetterLayout(prev.choices) || !isLetterLayout(next.choices)) return null;
  const prevText = new Map(prev.choices.map((c) => [c.id, (c.text || '').trim()]));
  const nextText = new Map(next.choices.map((c) => [c.id, (c.text || '').trim()]));
  const sig = (map) => JSON.stringify([...map.values()].sort());
  if (sig(prevText) !== sig(nextText)) return null;   // choice text edited — not a pure reorder
  const moved = [...nextText.keys()].filter((id) => prevText.get(id) !== nextText.get(id)).sort();
  if (moved.length === 0) return null;                // nothing moved
  const proseUnchanged =
    (prev.explanation || '') === (next.explanation || '') &&
    (prev.question || '') === (next.question || '');
  if (!proseUnchanged) return null;                   // author rewrote prose along with the reorder
  const refs = new Set([
    ...extractLetterReferences(next.explanation),
    ...extractLetterReferences(next.question),
    ...next.choices.flatMap((c) => extractLetterReferences(c.text)),
  ]);
  const stale = moved.filter((L) => refs.has(L));
  if (stale.length === 0) return null;
  return `Choices ${moved.join(', ')} were moved but the text still refers to ${stale.join(', ')} by letter. Rewrite the explanation to match the new order (or keep the original order).`;
}
